import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Check, Phone } from 'lucide-react';
import { OTP_TEXT, otpChannelLabel, otpChannelOf, responseAdvertisedOtp, useOtpDelivery } from '../lib/otpChannel';

// Admin-Telefonnummer: anzeigen + ÄNDERN nur mit Code (WhatsApp oder SMS, Twilio). Erstanlage erfolgt
// durch den Operator direkt; jede spätere Änderung läuft über /me/phone/request + /confirm.
export default function AdminPhone() {
  const [phone, setPhone] = useState(null);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('view'); // 'view' | 'enter' | 'code'
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const otp = useOtpDelivery();   // WHATSAPP-OTP-STANDARD-001: wohin der Code ging

  const load = () => api.get('/auth/me')
    .then(r => { setPhone(r.data.user?.phone || ''); setVerified(!!r.data.user?.phone_verified); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  // `preferSms`: „Lieber per SMS" — dieselbe Anfrage mit channel:'sms' (derselbe Code, ohne Wartezeit).
  const requestCode = async ({ preferSms = false } = {}) => {
    if (!/^\+?[0-9 ]{6,20}$/.test(newPhone.trim())) { toast.error('Bitte eine gültige Nummer eingeben (z. B. +49…).'); return false; }
    setBusy(true);
    try {
      const body = preferSms ? { phone: newPhone.trim(), channel: 'sms' } : { phone: newPhone.trim() };
      const response = await api.post('/auth/me/phone/request', body);
      if (response?.data?.sent !== true) {
        toast.error('Der Code konnte nicht zugestellt werden.');
        return false;
      }
      const channel = otpChannelOf(response.data, responseAdvertisedOtp(response));
      otp.receive(channel);
      setStep('code');
      if (channel === 'whatsapp') toast.success('Code per WhatsApp geschickt');
      else toast.success('Code per SMS geschickt');
      return true;
    } catch (e) {
      const data = e.response?.data;
      if (preferSms && data?.error === 'resend_too_soon') {
        toast.error(`Bitte warte noch ${Number(data.retry_after_seconds) || 60} Sekunden, bevor du einen neuen Code anforderst.`);
      } else {
        toast.error(data?.message || data?.error || 'Fehler');
      }
      return false;
    }
    finally { setBusy(false); }
  };
  const preferSms = async () => {
    if (busy || !otp.beginSmsSwitch()) return;   // Doppelklick-Schutz
    if (!(await requestCode({ preferSms: true }))) otp.failSmsSwitch();
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post('/auth/me/phone/confirm', { code: code.trim() });
      toast.success('Telefonnummer aktualisiert');
      setStep('view'); setNewPhone(''); setCode(''); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Code ungültig'); }
    finally { setBusy(false); }
  };
  const cancel = () => { setStep('view'); setNewPhone(''); setCode(''); otp.reset(); };

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, maxWidth: 520 };
  const btn = (bg, color = '#fff') => ({ padding: '11px 18px', background: bg, color, border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' });
  const inp = { width: '100%', maxWidth: 280, padding: '11px 14px', border: '1px solid #9C27B0', borderRadius: 9, fontSize: 16, boxSizing: 'border-box', marginBottom: 12 };

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Phone size={20} color="#9C27B0" />
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a' }}>Meine Telefonnummer</h2>
      </div>
      <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
        Die Nummer deines <b>eingeloggten</b> Admin-Kontos (zusätzlicher Wiederherstellungs-Kanal). Jeder Admin ändert
        hier seine <b>eigene</b> Nummer — eine <b>Änderung</b> muss immer per <b>Code</b> an die neue Nummer bestätigt
        werden, so kann niemand die Nummer unbemerkt austauschen.
      </p>

      {step === 'view' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#0a0a0a', fontFamily: 'monospace' }}>{phone || '— keine hinterlegt —'}</span>
            {phone && verified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3e5f5', color: '#7b1fa2', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                <Check size={14} /> verifiziert
              </span>
            )}
          </div>
          <button onClick={() => setStep('enter')} style={btn('#0a0a0a')}>Nummer ändern</button>
        </div>
      )}

      {step === 'enter' && (
        <div>
          <label style={{ display: 'block', fontSize: 13, color: '#374151', fontWeight: 600, marginBottom: 6 }}>Neue Telefonnummer (international, z. B. +49…)</label>
          <input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="+49 151 23456789" style={inp} inputMode="tel" />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => requestCode()} disabled={busy} style={btn('#9C27B0')}>Code anfordern</button>
            <button onClick={cancel} style={{ ...btn('transparent', '#4b5563'), padding: '11px 0' }}>Abbrechen</button>
          </div>
        </div>
      )}

      {step === 'code' && (
        <div>
          <p style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
            {otp.channel
              ? <>Wir haben dir den Code per {otpChannelLabel(otp.channel)} an <b>{newPhone}</b> geschickt. Code eingeben:</>
              : <>Wir haben einen 6-stelligen Code an <b>{newPhone}</b> geschickt. Code eingeben:</>}
          </p>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} maxLength={6} inputMode="numeric" placeholder="123456"
            style={{ ...inp, maxWidth: 180, letterSpacing: 6, textAlign: 'center', fontSize: 16 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={confirm} disabled={busy || code.length !== 6} style={btn(code.length === 6 ? '#9C27B0' : '#d1d5db')}>Bestätigen</button>
            {otp.showsPreferSms && (
              <button type="button" onClick={preferSms} disabled={busy || otp.switching} title={OTP_TEXT.preferSmsHint}
                style={{ ...btn('transparent', '#4b5563'), padding: '11px 0' }}>{OTP_TEXT.preferSms}</button>
            )}
            <button onClick={cancel} style={{ ...btn('transparent', '#4b5563'), padding: '11px 0' }}>Abbrechen</button>
          </div>
        </div>
      )}

      {phone === null && <p style={{ color: '#6b7280', fontSize: 13 }}>Lädt …</p>}
    </div>
  );
}
